import {
  Autocomplete,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  OutlinedInput,
  InputAdornment,
  Tooltip,
  Stack,
  Chip,
  Alert,
} from '@mui/material';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useCustomerActions } from 'src/context/providers/CustomerContext';
import { PATH_DASHBOARD } from 'src/config-global';
import { CloseIcon } from 'yet-another-react-lightbox';
import { usePalletActions } from 'src/context/providers/PalleteContext';
import { useDeliveryAreaActions } from 'src/context/providers/DeliveryAreaContext';
import OrderListPhysical from 'src/custom_components/orderList/PhysicalOrderList';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'react-toastify';
import {
  PHYSICAL_DIRECTIONS,
  PHYSICAL_DIRECTION_LABELS,
  ROUTE_COUNTRIES,
  countryLabel,
} from 'src/utils/physical-api';
import { AREA_DELIVERY_TYPES } from '../deliveryArea/view/data';
import {
  DELIVERY_OPTIONS_BY_DIRECTION,
  ORIGIN_WAREHOUSE_STATUS,
  initialStatusesFor,
  pickupRegions,
  status_colors,
} from './view/data';
import ParcelGenerationModal from './order-tracking-generator';
import { InfoGrid } from './order-creation-steps';
import { DirectionFlags } from './direction-picker-modal';

const addressLine = (address) =>
  [address?.address1, address?.address2, address?.city, address?.state, address?.zipCode]
    .filter(Boolean)
    .join(', ');

const fullName = (person) =>
  `${person?.first_name || person?.name || ''} ${person?.last_name || person?.lname || ''}`.trim();

const OrderCreation = ({ isDrawerOpen, handleDrawerToggle, onOrdersCreated, direction }) => {
  const { getPhysicalCustomers, getSenderById } = useCustomerActions();
  const { getPhysicalPallets } = usePalletActions();
  const containerStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    zIndex: 2,
    top: 0,
    left: 0,
    display: isDrawerOpen ? 'block' : 'none',
    cursor: 'pointer',
  };

  const handleOutsideClick = (event) => {
    if (event.target === event.currentTarget) {
      handleDrawerToggle();
      // Clear();
    }
  };

  const [customer, setCustomere] = useState();
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [CustomerOptions, setCustomerOptions] = useState([]);
  const [primaryReceiver, setPrimaryReceiver] = useState(null);
  const [ReceiverOptions, setReceiverOptions] = useState([]);
  const [hiddenReceivers, setHiddenReceivers] = useState(0);

  const [parcelModalOpen, setParcelModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    direction: PHYSICAL_DIRECTIONS.USA_GE,
    status: 'RECEIVED IN US WAREHOUSE',
    primaryReceiverId: null,
    customer_id: null,
    delivery_type: '',
    orders: [],
    pickup_area: '',
    pickup_price: null,
    delivery_area_id: null,
    promotion: null,
    group_id: null,
  });

  const { getActiveDeliveryAreas } = useDeliveryAreaActions();
  const [deliveryAreas, setDeliveryAreas] = useState([]);
  const needsDeliveryArea = AREA_DELIVERY_TYPES.includes(formData.delivery_type);
  const selectedArea = deliveryAreas.find((area) => area.id === formData.delivery_area_id);

  useEffect(() => {
    setDeliveryAreas([]);
    if (!needsDeliveryArea) return;
    getActiveDeliveryAreas(formData.delivery_type).then(setDeliveryAreas);
  }, [needsDeliveryArea, formData.delivery_type, getActiveDeliveryAreas]);

  const [pallet, setPallete] = useState({ id: '', tracking: '' });
  const [loadingPallet, setLoadingPallet] = useState(false);
  const [palletOptions, setPalletOptions] = useState([]);

  const Clear = () => {
    setCustomere(null);
    setCustomerOptions([]);
    setPrimaryReceiver(null);
    setReceiverOptions([]);
    setHiddenReceivers(0);
    setPallete(null);
    setPalletOptions([]);
    setFormData({
      direction: PHYSICAL_DIRECTIONS.USA_GE,
      status: 'RECEIVED IN US WAREHOUSE',
      primaryReceiverId: null,
      customer_id: null,
      delivery_type: '',
      orders: [],
      pickup_area: '',
      pickup_price: null,
      delivery_area_id: null,
      promotion: null,
      group_id: null,
      pallet_id: null,
    });
  };

  const fetchCustomeres = useCallback(
    async (filters) => {
      setLoadingCustomer(true);
      try {
        const response = await getPhysicalCustomers(filters);
        setCustomerOptions(response.customers);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCustomer(false);
      }
    },
    [getPhysicalCustomers]
  );

  const handlePalletChange = (event, newValue) => {
    setFormData({
      ...formData,
      pallet_id: newValue?.id || null,
    });
    setPallete(newValue);
  };

  const handleAttachParcels = (newParcels, groupId) => {
    setFormData((prev) => ({
      ...prev,
      orders: [...(prev.orders || []), ...newParcels],
      group_id: groupId ?? prev.group_id,
    }));
  };
  const handleDrawerToggleCustomerEdit = () => {
    const url = `${PATH_DASHBOARD.customer.root}/${customer.id}`;
    window.open(url, '_blank');
  };

  const DrawerConfig = {
    SubNavigation: false,
    anchor: 'right',
    width: '80%',
    drawerHeaderText: 'Create Order',
    backgroundColor: 'red',
    SubNav: true,
  };

  const handleCustomerInputChange = debounce((event, newInputValue) => {
    const filters = {
      name: newInputValue,
      country: ROUTE_COUNTRIES[formData.direction].origin,
      take: 5,
    };
    fetchCustomeres(filters);
  }, 500);

  const fetchPalletes = useCallback(
    async (filters) => {
      setLoadingPallet(true);
      try {
        const response = await getPhysicalPallets({
          ...filters,
          direction: formData.direction,
          status: 'CREATED',
        });
        setPalletOptions(response.pallets);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingPallet(false);
      }
    },
    [getPhysicalPallets, formData.direction]
  );

  useEffect(() => {
    if (!isDrawerOpen) return;
    setPallete(null);
    setFormData((prev) => ({ ...prev, pallet_id: null }));
    fetchPalletes({ take: 10 });
  }, [fetchPalletes, isDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen) {
      Clear();
      setParcelModalOpen(false);
    } else if (direction) {
      setFormData((prev) => ({ ...prev, direction, status: ORIGIN_WAREHOUSE_STATUS[direction] }));
    }
  }, [isDrawerOpen, direction]);

  const handlePalletInputChange = debounce((event, newInputValue) => {
    fetchPalletes({ tracking: newInputValue, take: 10 });
  }, 500);

  const ordersAttached = formData?.orders?.length > 0;
  const routeCountries = ROUTE_COUNTRIES[formData.direction];
  const senderCountry = countryLabel(routeCountries.origin);
  const receiverCountry = countryLabel(routeCountries.destination);
  const hasAcceptedActiveTerms = (customer?.termsAndPoliciesAcceptance || []).some(
    (t) => t?.termsAndConditions?.active && t?.status === 'ACCEPTED'
  );
  const missingSteps = [
    !customer && 'sender',
    customer && !hasAcceptedActiveTerms && 'accepted terms',
    !formData.primaryReceiverId && 'receiver',
    !formData.delivery_type && 'delivery type',
    needsDeliveryArea && !formData.delivery_area_id && 'delivery area',
    !formData.pallet_id && 'pallet',
  ].filter(Boolean);

  return (
    <div
      role="button"
      tabIndex={0}
      style={containerStyle}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          handleDrawerToggle();
        }
      }}
      onClick={handleOutsideClick}
    >
      <Drawer
        variant="persistent"
        anchor={DrawerConfig.anchor}
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: { width: { lg: DrawerConfig.width, xs: '100%', md: '100%', paddingBottom: '20px' } },
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '7px 16px',
            borderBottom: '1px solid rgba(220, 220, 220, 0.2)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h6">{DrawerConfig.drawerHeaderText}</Typography>
            <Chip
              variant="outlined"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DirectionFlags direction={formData.direction} size={18} />
                  <span>{PHYSICAL_DIRECTION_LABELS[formData.direction]}</span>
                </Stack>
              }
            />
          </Stack>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '20px',
            }}
          >
            <IconButton onClick={handleDrawerToggle}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <Stack spacing={2.5} sx={{ px: 3, py: 2.5 }}>
          <Stack spacing={1.5}>
            <Typography variant="overline" color="text.secondary">
              Sender · {senderCountry}
            </Typography>
            <Autocomplete
              options={CustomerOptions ?? []}
              value={customer ?? null}
              loading={loadingCustomer}
              disabled={ordersAttached}
              getOptionLabel={(option) => {
                const first = option?.name || option?.first_name || '';
                const last = option?.lname || option?.last_name || '';
                const idn = option?.id_number ? ` • ${option.id_number}` : '';
                const phone = option?.phone ? ` • ${option.phone}` : '';
                return `${first} ${last}${idn}${phone}`.trim();
              }}
              isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
              onChange={async (event, newValue) => {
                setPrimaryReceiver(null);
                setReceiverOptions([]);
                setHiddenReceivers(0);
                setFormData((prev) => ({
                  ...prev,
                  customer_id: newValue?.id || null,
                  primaryReceiverId: null,
                }));
                setCustomere(newValue);
                if (!newValue) return;
                try {
                  const sender = await getSenderById(newValue.id);
                  setCustomere(sender);
                  const receivers = sender?.recievers || [];
                  const inDestination = receivers.filter(
                    (receiver) => receiver.country === routeCountries.destination
                  );
                  setReceiverOptions(inDestination);
                  setHiddenReceivers(receivers.length - inDestination.length);
                } catch (error) {
                  toast.error('Could not load sender receivers');
                }
              }}
              onInputChange={(event, newInputValue) => handleCustomerInputChange(event, newInputValue)}
              renderOption={(props, option) => {
                const first = option?.name || option?.first_name || '';
                const last = option?.lname || option?.last_name || '';
                const idn = option?.id_number || '';
                const email = option?.email || '';
                const phone = option?.phone || '';
                return (
                  <li {...props} key={option?.id ?? `${first}-${last}-${idn}`}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600 }}>
                        {first} {last} {idn && <span style={{ color: '#637381' }}>• {idn}</span>}
                      </span>
                      {(email || phone) && (
                        <span style={{ fontSize: 12, color: '#637381' }}>
                          {[email, phone].filter(Boolean).join(' • ')}
                        </span>
                      )}
                    </div>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search sender by name, ID number or phone"
                  variant="filled"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCustomer ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {customer && (
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ flex: 1 }}>
                      {fullName(customer)}
                    </Typography>
                    <Chip
                      size="small"
                      color={hasAcceptedActiveTerms ? 'success' : 'warning'}
                      label={hasAcceptedActiveTerms ? 'Terms accepted' : 'Terms not accepted'}
                    />
                    <Tooltip title="Edit sender">
                      <IconButton size="small" onClick={handleDrawerToggleCustomerEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <InfoGrid
                    items={[
                      {
                        label: 'Name (KA)',
                        value: `${customer.first_name_ka ?? ''} ${customer.last_name_ka ?? ''}`.trim(),
                      },
                      { label: 'ID Number', value: customer.id_number },
                      { label: 'Room', value: customer.room_number },
                      { label: 'Resident', value: customer.isNotResident ? 'No' : 'Yes' },
                      { label: 'Phone', value: customer.phone },
                      { label: 'Secondary phone', value: customer.phone_secondary },
                      { label: 'Country', value: countryLabel(customer.country) },
                      { label: 'Email', value: customer.email },
                      { label: 'Address', value: addressLine(customer.personalAddress?.[0]) },
                    ]}
                  />
                  {!hasAcceptedActiveTerms && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Orders can be created after the sender accepts the latest terms.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>

          {customer && (
            <Stack spacing={1.5}>
              <Typography variant="overline" color="text.secondary">
                Receiver · {receiverCountry}
              </Typography>
              {ReceiverOptions.length ? (
                <Autocomplete
                  options={ReceiverOptions}
                  value={primaryReceiver}
                  disabled={ordersAttached}
                  onChange={(e, val) => {
                    setPrimaryReceiver(val);
                    setFormData((prev) => ({
                      ...prev,
                      primaryReceiverId: val?.id || null,
                    }));
                  }}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                  getOptionLabel={(option) => {
                    const phone = option?.phone ? ` • ${option.phone}` : '';
                    return `${fullName(option)}${phone}`;
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600 }}>
                          {fullName(option)}{' '}
                          {option?.phone && <span style={{ color: '#637381' }}>• {option.phone}</span>}
                          {option?.isPrimary && (
                            <Chip size="small" label="Primary" sx={{ ml: 1, height: 18 }} />
                          )}
                        </span>
                        {option?.email && (
                          <span style={{ fontSize: 12, color: '#637381' }}>{option.email}</span>
                        )}
                      </div>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Select receiver" variant="filled" />
                  )}
                />
              ) : (
                <Alert severity="info">
                  This sender has no receivers in {receiverCountry} yet. Add one in the Receiver tab.
                </Alert>
              )}
              {hiddenReceivers > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {hiddenReceivers} receiver(s) hidden: their country is not {receiverCountry} or not set.
                </Typography>
              )}

              {primaryReceiver && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {fullName(primaryReceiver)}
                    </Typography>
                    <InfoGrid
                      items={[
                        { label: 'ID Number', value: primaryReceiver.id_number },
                        { label: 'Phone', value: primaryReceiver.phone },
                        { label: 'Email', value: primaryReceiver.email },
                        { label: 'City', value: primaryReceiver.personalAddress?.[0]?.city },
                        { label: 'Address', value: addressLine(primaryReceiver.personalAddress?.[0]) },
                      ]}
                    />
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}

          {primaryReceiver && (
            <Stack spacing={1.5}>
              <Typography variant="overline" color="text.secondary">
                Shipment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="delivery-label-1">Delivery Type</InputLabel>
                    <Select
                      labelId="delivery-label-1"
                      value={formData.delivery_type}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          delivery_type: e.target.value,
                          delivery_area_id: null,
                        }))
                      }
                      label="Delivery Type"
                      disabled={ordersAttached}
                    >
                      {DELIVERY_OPTIONS_BY_DIRECTION[formData.direction].map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {needsDeliveryArea && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Delivery Area"
                        value={formData.delivery_area_id ?? ''}
                        disabled={ordersAttached}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, delivery_area_id: e.target.value }))
                        }
                        helperText={deliveryAreas.length ? '' : 'No active areas — add one in Delivery Areas'}
                      >
                        {deliveryAreas.map((area) => (
                          <MenuItem key={area.id} value={area.id}>
                            {area.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Delivery fee (from area)"
                        value={
                          selectedArea ? `$${Number(selectedArea.price_usd).toFixed(2)} per group` : ''
                        }
                        InputProps={{ readOnly: true }}
                        disabled={!selectedArea}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={palletOptions}
                    getOptionLabel={(option) => option.tracking || ''}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600 }}>{option.tracking}</span>
                          <span style={{ fontSize: 12, color: '#637381' }}>
                            {option.parcel_count} parcels · {option.total_weight} kg
                            {option.airwayBill?.tracking ? ` · AWB ${option.airwayBill.tracking}` : ''}
                          </span>
                        </div>
                      </li>
                    )}
                    noOptionsText={`No open ${PHYSICAL_DIRECTION_LABELS[formData.direction]} pallets`}
                    value={pallet}
                    onChange={handlePalletChange}
                    onInputChange={handlePalletInputChange}
                    loading={loadingPallet}
                    disabled={ordersAttached}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pallet Tracking"
                        variant="filled"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingPallet ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      label="Status"
                      style={{
                        backgroundColor: status_colors[formData.status]
                          ? status_colors[formData.status][0]
                          : '',
                        color: status_colors[formData.status] ? status_colors[formData.status][1] : '',
                      }}
                    >
                      {initialStatusesFor(formData.direction).map((i) => (
                        <MenuItem key={i} value={i}>
                          {i}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Promotion (%)"
                    type="number"
                    value={formData.promotion ?? ''}
                    disabled={ordersAttached}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, promotion: Number(e.target.value) }))
                    }
                  />
                </Grid>

                {formData.direction === PHYSICAL_DIRECTIONS.USA_GE && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel id="pickup-area-label">Pick-up Area</InputLabel>
                        <Select
                          labelId="pickup-area-label"
                          label="Pick-up Area"
                          value={formData.pickup_area || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              pickup_area: e.target.value,
                            }))
                          }
                          disabled={ordersAttached}
                          input={
                            <OutlinedInput
                              label="Pick-up Area"
                              endAdornment={
                                formData.pickup_area && (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="clear selection"
                                      edge="end"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFormData((prev) => ({ ...prev, pickup_area: null }));
                                      }}
                                      sx={{ mr: 2 }}
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }
                            />
                          }
                        >
                          {pickupRegions.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Pick-up Price"
                        type="number"
                        value={formData.pickup_price ?? ''}
                        disabled={ordersAttached}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, pickup_price: Number(e.target.value) }))
                        }
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Stack>
          )}

          {customer && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
              {missingSteps.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Missing: {missingSteps.join(', ')}
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={() => setParcelModalOpen(true)}
                disabled={missingSteps.length > 0}
              >
                Generate Parcels
              </Button>
            </Stack>
          )}
        </Stack>

        <ParcelGenerationModal
          open={parcelModalOpen}
          onClose={() => setParcelModalOpen(false)}
          deliveryType={formData.delivery_type}
          receiverOptions={ReceiverOptions || []}
          onAttach={(parcels, groupId) => {
            handleAttachParcels(parcels, groupId);
            setParcelModalOpen(false);
            handleDrawerToggle();
            onOrdersCreated?.();
          }}
          aRow={formData}
        />
        {formData.orders.length ? <OrderListPhysical aRow={formData} /> : null}
      </Drawer>
    </div>
  );
};

OrderCreation.propTypes = {
  isDrawerOpen: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  onOrdersCreated: PropTypes.func,
  direction: PropTypes.oneOf(Object.values(PHYSICAL_DIRECTIONS)),
};

export default OrderCreation;
